import React, { useState, useEffect } from "react";
import api from "../../axios/api";
import "../../assets/NewFiles/Css/Dashboard.css";
import { useNavigate } from "react-router-dom";
import CreateUser from "./CreateUser";
import { ToastContainer, toast } from "react-toastify";
import {
  Tabs,
  Tab,
  Box,
  TableContainer,
  Table,
  Paper,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
} from "@mui/material";
import { FaEye, FaEyeSlash, FaCheckCircle, FaEdit, FaBan, FaTimes, FaSearch } from "react-icons/fa";
import * as yup from 'yup';
import Loader from "../common/Loader";
import Grid from "../common/Grid";

// Define validation schema using Yup
const validationSchema = yup.object({
  full_name: yup
    .string()
    .required('Full Name is required'),
  userEmail: yup
    .string()
    .email('Enter a valid email')
    .required('Email is required'),
  phoneNumber: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]+$/, 'Must be only digits')
    .min(10, 'Must be exactly 10 digits')
    .max(10, 'Must be exactly 10 digits'),
});

const ViewAllUsers = () => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();

  //For filters and pagination
  const [companies, setCompanies] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [userType, setUserType] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const rowsPerPage = 10;
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Edit user state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({
    full_name: "",
    userEmail: "",
    phoneNumber: "",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await api.get("/api/admin/getUsers");
        const allUsers = response.data.users.filter(
          (user) =>
            user.role_code !== "admin" && user.role_code !== "superadmin"
        );
        setUsers(allUsers);
        setFilteredUsers(allUsers);
        setCompanies(response.data.companies);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    let result = users;


    if (search.trim()) {
      const lowerSearch = search.toLowerCase().trim();

      result = result.filter((user) => {
        const fullName = user.full_name?.toLowerCase() || "";
        const email = user.userEmail?.toLowerCase() || "";
        const searchWords = lowerSearch.split(" ");
        return searchWords.some(
          (word) =>
            fullName.includes(word) || email.includes(word)
        );
      });
    }

    if (userType) {
      result = result.filter(
        (user) => user.role_code.toLowerCase() === userType.toLowerCase()
      );
    }

    if (selectedOrg) {
      result = result.filter((user) => user.company_name === selectedOrg);
    }

    setFilteredUsers(result);
    setPage(1);
  }, [search, userType, selectedOrg, users]);

  const handleToggleShowOnApp = async (userId) => {
    setIsLoading(true);
    try {
      await api.put(`/api/admin/user/${userId}/toggle-show-on-app`);
      const updatedUsers = users.map((user) =>
        user.id === userId ? { ...user, show_on_app: !user.show_on_app } : user
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error("Error toggling show_on_app:", error);
    } finally {
      setIsLoading(false);

    }
  };

  const handleToggleVerification = async (userId) => {
    try {
      setIsLoading(true);
      await api.put(`/api/admin/verifyUser`, { userId });
      const updatedUsers = users.map((user) =>
        user.id === userId ? { ...user, isVerified: !user.isVerified } : user
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error("Error toggling show_on_app:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBan = async (userId) => {
    setIsLoading(true);
    try {
      await api.put(`/api/admin/ban`, { userId });
      const updatedUsers = users.map((user) =>
        user.id === userId ? { ...user, is_status: !user.is_status } : user
      );
      setUsers(updatedUsers);
    } catch (error) {
      console.error("Error toggling show_on_app:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Calculate pagination
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  const pageCount = Math.ceil(filteredUsers.length / rowsPerPage);

  //function for edit User
  const handleEditClick = (user) => {
    setEditingUser(user);
    setEditFormData({
      full_name: user.full_name,
      userEmail: user.userEmail,
      phoneNumber: user.phoneNumber,
    });
    setFormErrors({});
    setTouched({});
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditingUser(null);
    setEditFormData({
      full_name: "",
      userEmail: "",
      phoneNumber: "",
    });
    setFormErrors({});
    setTouched({});
  };

  const handleEditSubmit = async () => {
    setIsLoading(true);
    try {
      await validationSchema.validate(editFormData, { abortEarly: false });
      await api.put(`/api/admin/EditUser/${editingUser.id}`, editFormData);

      // Update the users state with the edited data
      const updatedUsers = users.map((user) =>
        user.id === editingUser.id
          ? { ...user, full_name: editFormData.full_name, userEmail: editFormData.userEmail, phoneNumber: editFormData.phoneNumber }
          : user
      );

      setUsers(updatedUsers);
      toast.success("User updated successfully!");
      handleEditClose();
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        // Handle validation errors
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setFormErrors(newErrors);
        // Set all fields as touched to show errors
        setTouched({
          full_name: true,
          userEmail: true,
          phoneNumber: true,
        });
      } else {
        toast.error(`Failed to update user ${error.response.data.message}`);
      }
    } finally {
      setIsLoading(false);

    }
  };

  return (
    <div>
      <Box sx={{ width: "100%", margin: "auto", padding: 2 }}>
        <div style={{ position: 'relative' }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3 }}>
            <Box
              sx={{
                width: 4,
                height: 28,
                bgcolor: "#10b981",
                borderRadius: "4px",
                flexShrink: 0,
              }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontFamily: "'Plus Jakarta Sans', 'Outfit', 'Inter', sans-serif",
                fontSize: { xs: "1.4rem", sm: "1.75rem" },
                fontWeight: 700,
                color: "#002F43",
                letterSpacing: "-0.02em",
                m: 0,
                lineHeight: 1.2,
              }}
            >
              User Management
            </Typography>
          </Box>
          <Box sx={{ marginBottom: 2, display: "flex", justifyContent: "center" }}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{
              '& .MuiTabs-indicator': {
                display: 'none',
              },
            }}>
              <Tab
                label="User List"
                sx={{
                  color: 'black',
                  minHeight: '48px',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  paddingX: 3,
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: '#4caf50 !important',
                    color: 'white !important',
                  },
                  '&:hover': {
                    color: 'white !important',
                    backgroundColor: '#4caf50',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'scale(1.03)',
                  },
                }}
              />
              <Tab
                label="Create User"
                sx={{
                  color: 'black',
                  minHeight: '48px',
                  fontWeight: 600,
                  textTransform: 'none',
                  transition: 'all 0.3s ease',
                  paddingX: 3,
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: '#4caf50 !important',
                    color: 'white !important',
                  },
                  '&:hover': {
                    color: 'white !important',
                    backgroundColor: '#4caf50',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    transform: 'scale(1.03)',
                  },
                }}
              />
            </Tabs>

          </Box>
          <Box sx={{ padding: 2 }}>
            {tabValue === 0 && (
              <>
                {/* Modern Search & Filter Box */}
                <div className="bg-white p-3.5 sm:p-4 rounded-xl shadow-xs border border-gray-200/80 mb-5">
                  <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
                    {/* Search Input Bar */}
                    <div className="relative flex-1">
                      <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-emerald-600 text-sm pointer-events-none" />
                      <input
                        type="text"
                        placeholder="Search users by name or email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full !pl-10 !pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 transition-all outline-none"
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                      />
                      {search && (
                        <button
                          type="button"
                          onClick={() => setSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs p-1 cursor-pointer"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>

                    {/* Filter Selects */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <FormControl sx={{ minWidth: 140 }} size="small">
                        <InputLabel>User Type</InputLabel>
                        <Select
                          value={userType}
                          label="User Type"
                          onChange={(e) => setUserType(e.target.value)}
                          sx={{ bgcolor: 'grey.50', borderRadius: 1 }}
                        >
                          <MenuItem value="">All Types</MenuItem>
                          <MenuItem value="ngo">NGO</MenuItem>
                          <MenuItem value="individual">Individual</MenuItem>
                          <MenuItem value="corporate">Corporate</MenuItem>
                          <MenuItem value="employee">Employee</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl sx={{ minWidth: 160 }} size="small">
                        <InputLabel>Organization</InputLabel>
                        <Select
                          value={selectedOrg}
                          label="Organization"
                          onChange={(e) => setSelectedOrg(e.target.value)}
                          sx={{ bgcolor: 'grey.50', borderRadius: 1 }}
                        >
                          <MenuItem value="">All Organizations</MenuItem>
                          {companies.map((company) => (
                            <MenuItem key={company.id} value={company.company_name}>
                              {company.company_name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </div>
                </div>

                <Grid
                  columns={[
                    {
                      header: "Full Name",
                      accessor: "full_name",
                      sortable: true,
                    },
                    {
                      header: "Email",
                      accessor: "userEmail",
                      sortable: true,
                      render: (val, user) => (
                        <div style={{ display: "flex", alignItems: "center", gap: "8px"}}>
                          {user.isVerified ? (
                            <FaCheckCircle
                              color="green"
                              title="Verified User"
                            />
                          ) : (
                            <FaCheckCircle
                              color="red"
                              title="Unverified User"
                            />
                          )}
                          <span style={{color:"black"}}>{val}</span>
                        </div>
                      ),
                    },
                    {
                      header: "Role",
                      accessor: "role_code",
                      sortable: true,
                    },
                    {
                      header: "Total",
                      accessor: "totalTrees",
                      sortable: true,
                      align: "center",
                      render: (val) => val ?? 0,
                    },
                    {
                      header: "Geotagged",
                      accessor: "geoLocatedTreesCount",
                      sortable: true,
                      align: "center",
                      render: (val) => val ?? 0,
                    },
                    {
                      header: "Verified",
                      accessor: "verifiedTrees",
                      sortable: true,
                      align: "center",
                      render: (val) => val ?? 0,
                    },
                    {
                      header: "Deleted",
                      accessor: "deletedTrees",
                      sortable: true,
                      align: "center",
                      render: (val) => val ?? 0,
                    },
                    {
                      header: "Last Login",
                      accessor: "lastLogin",
                      sortable: true,
                      align: "center",
                      render: (val) =>
                        val ? new Date(val).toLocaleString() : "-",
                    },
                    {
                      header: "Actions",
                      align: "center",
                      render: (_, user) => (
                        <div className="aranya-grid-actions-cell" style={{ justifyContent: "center" }}>
                          <button
                            onClick={() => handleEditClick(user)}
                            style={{
                              padding: "4px 8px",
                              height: "28px",
                              border: "1px solid #cbd5e1",
                              borderRadius: "4px",
                              cursor: "pointer",
                              background: "#f8fafc",
                            }}
                            className="toggle-btn"
                            title="Edit User"
                          >
                            <FaEdit color="#334155" />
                          </button>
                          <button
                            onClick={() => handleToggleShowOnApp(user.id)}
                            style={{
                              padding: "4px 8px",
                              height: "28px",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                            className={`toggle-btn ${
                              user.show_on_app ? "hide-btn" : "show-btn"
                            }`}
                            title={
                              user.show_on_app ? "Hide from App" : "Show on app"
                            }
                          >
                            {user.show_on_app ? (
                              <FaEyeSlash color="white" />
                            ) : (
                              <FaEye color="white" />
                            )}
                          </button>

                          <button
                            onClick={() => handleToggleVerification(user.id)}
                            style={{
                              padding: "4px 8px",
                              height: "28px",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                            className={`toggle-btn ${
                              user.isVerified ? "show-btn" : "hide-btn"
                            }`}
                            title={
                              user.isVerified ? "Unverify User" : "Verify User"
                            }
                          >
                            <FaCheckCircle color="white" />
                          </button>

                          <button
                            onClick={() => handleToggleBan(user.id)}
                            style={{
                              padding: "4px 8px",
                              height: "28px",
                              borderRadius: "4px",
                              cursor: "pointer",
                            }}
                            className={`toggle-btn ${
                              user.is_status ? "show-btn" : "hide-btn"
                            }`}
                            title={user.is_status ? "Ban User" : "Unban User"}
                          >
                            <FaBan color="white" />
                          </button>
                        </div>
                      ),
                    },
                  ]}
                  data={filteredUsers}
                  variant="navy"
                  pagination={true}
                  pageSize={rowsPerPage}
                  pageSizeOptions={[5, 10, 20, 50]}
                  rowKey="id"
                  emptyMessage="No users found"
                />
              </>
            )}
            {tabValue === 1 && <CreateUser />}
          </Box>
          <Dialog open={editDialogOpen} onClose={handleEditClose}>
            <DialogTitle>Edit User</DialogTitle>
            <p style={{ fontSize: '13px', padding: '0px 24px' }}>NOTE: fields marked with <span style={{ color: 'red' }}>*</span> are mandatory to input.</p>
            <DialogContent>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                <TextField
                  label="Full Name*"
                  value={editFormData.full_name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, full_name: e.target.value })
                  }
                  error={touched.full_name && Boolean(formErrors.full_name)}
                  helperText={touched.full_name && formErrors.full_name}
                  fullWidth
                  multiline={true}
                  rows={1}
                />
                <TextField
                  label="Email*"
                  value={editFormData.userEmail}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, userEmail: e.target.value })
                  }
                  error={touched.userEmail && Boolean(formErrors.userEmail)}
                  helperText={touched.userEmail && formErrors.userEmail}
                  fullWidth
                  multiline={true}
                  rows={1}
                />
                <TextField
                  label="phoneNumber*"
                  value={editFormData.phoneNumber}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, phoneNumber: e.target.value })
                  }
                  error={touched.phoneNumber && Boolean(formErrors.phoneNumber)}
                  helperText={touched.phoneNumber && formErrors.phoneNumber}
                  fullWidth
                  multiline={true}
                  rows={1}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleEditClose}>Cancel</Button>
              <Button onClick={handleEditSubmit} variant="contained" color="primary">
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </Box>
    </div>
  );
};

export default ViewAllUsers;
